import React, { useState } from 'react';
import { FileText, Eye, X, AlertCircle, Clock, CheckCircle, Send } from 'lucide-react';
import NgoLayout from '../../../../layouts/Ngo/NgoLayout';
import '../../../../assets/css/Ngo/controlpanelCSS/ngodisputes.css';

const NgoDisputes = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [resolveComment, setResolveComment] = useState('');
  const [forwardReason, setForwardReason] = useState('');

  // Disputes Data
  const disputes = [
    {
      id: 1,
      studentName: 'Ayesha Khan',
      caseId: 'CASE-123',
      caseTitle: 'School Fee Support',
      issueType: 'Payment',
      submittedDate: '15 Jan 2026',
      status: 'new',
      description: 'Main ne apni fee ki request 2 hafte pehle submit ki thi lekin abhi tak koi payment nahi mili. Please is issue ko dekhen.',
      attachment: 'payment_receipt.pdf',
      studentEmail: 'ayesha@example.com',
      studentPhone: '+92 300 1234567'
    },
    {
      id: 2,
      studentName: 'Ali Raza',
      caseId: 'CASE-124',
      caseTitle: 'Book Allowance',
      issueType: 'Verification',
      submittedDate: '14 Jan 2026',
      status: 'in-review',
      description: 'Meri documents verify nahi ho rahi hain. Main ne sahi documents upload kiye hain lekin verification pending hai.',
      attachment: null,
      studentEmail: 'ali@example.com',
      studentPhone: '+92 301 2234567'
    },
    {
      id: 3,
      studentName: 'Sara Ahmed',
      caseId: 'CASE-125',
      caseTitle: 'Tuition Fee',
      issueType: 'Delay',
      submittedDate: '12 Jan 2026',
      status: 'forwarded',
      description: 'Payment delay ho rahi hai. Mujhe urgently fee submit karni hai.',
      attachment: 'fee_voucher.jpg',
      studentEmail: 'sara@example.com',
      studentPhone: '+92 302 3234567'
    },
    {
      id: 4,
      studentName: 'Hassan Ali',
      caseId: 'CASE-126',
      caseTitle: 'Transport Fee',
      issueType: 'Behavior',
      submittedDate: '10 Jan 2026',
      status: 'resolved',
      description: 'NGO staff ne mujhe proper information nahi di. Behavior achi nahi thi.',
      attachment: null,
      studentEmail: 'hassan@example.com',
      studentPhone: '+92 303 4234567'
    }
  ];

  // Filter disputes
  const filteredDisputes = disputes.filter(dispute => 
    activeFilter === 'all' ? true : dispute.status === activeFilter
  );

  // Get status badge class and text
  const getStatusInfo = (status) => {
    const statusMap = {
      'new': { class: 'new', text: 'New', icon: AlertCircle },
      'in-review': { class: 'in-review', text: 'In Review', icon: Clock },
      'forwarded': { class: 'forwarded', text: 'Forwarded', icon: Send },
      'resolved': { class: 'resolved', text: 'Resolved', icon: CheckCircle }
    };
    return statusMap[status] || statusMap['new'];
  };

  // Handle view dispute
  const handleViewDispute = (dispute) => {
    setSelectedDispute(dispute);
  };

  // Handle mark as in review
  const handleMarkInReview = () => {
    alert(`Dispute #${selectedDispute.id} marked as In Review`);
    setSelectedDispute({ ...selectedDispute, status: 'in-review' });
  };

  // Handle resolve
  const handleResolve = () => {
    if (!resolveComment.trim()) {
      alert('Please enter resolution comments');
      return;
    }
    alert(`Dispute #${selectedDispute.id} resolved with comment: ${resolveComment}`);
    setResolveComment('');
    setShowResolveModal(false);
    setSelectedDispute(null);
  };

  // Handle forward to admin
  const handleForwardToAdmin = () => {
    if (!forwardReason.trim()) {
      alert('Please enter reason for forwarding');
      return;
    }
    alert(`Dispute #${selectedDispute.id} forwarded to admin. Reason: ${forwardReason}`);
    setForwardReason('');
    setShowForwardModal(false);
    setSelectedDispute(null);
  };

  return (
    <NgoLayout>
      <div className="ngo-disputes-container">
        <div className="ngo-disputes-wrapper">
          {/* Header */}
          <div className="ngo-disputes-header">
            <div>
              <h1 className="ngo-disputes-title">Student Disputes</h1>
              <p className="ngo-disputes-subtitle">Complaints received from students</p>
            </div>
          </div>

          {/* Filters */}
          <div className="ngo-disputes-filters">
            <button 
              className={`ngo-disputes-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All Disputes
            </button>
            <button 
              className={`ngo-disputes-filter-btn ${activeFilter === 'new' ? 'active' : ''}`}
              onClick={() => setActiveFilter('new')}
            >
              New
            </button>
            <button 
              className={`ngo-disputes-filter-btn ${activeFilter === 'in-review' ? 'active' : ''}`}
              onClick={() => setActiveFilter('in-review')}
            >
              In Progress
            </button>
            <button 
              className={`ngo-disputes-filter-btn ${activeFilter === 'forwarded' ? 'active' : ''}`}
              onClick={() => setActiveFilter('forwarded')}
            >
              Forwarded
            </button>
            <button 
              className={`ngo-disputes-filter-btn ${activeFilter === 'resolved' ? 'active' : ''}`}
              onClick={() => setActiveFilter('resolved')}
            >
              Resolved
            </button>
          </div>

          {/* Disputes Table */}
          <div className="ngo-disputes-card">
            <div className="ngo-disputes-table-wrapper">
              <table className="ngo-disputes-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Case ID / Title</th>
                    <th>Issue Type</th>
                    <th>Submitted Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDisputes.map((dispute) => {
                    const statusInfo = getStatusInfo(dispute.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr key={dispute.id}>
                        <td className="ngo-disputes-student-name">{dispute.studentName}</td>
                        <td>
                          <div className="ngo-disputes-case-info">
                            <span className="ngo-disputes-case-id">{dispute.caseId}</span>
                            <span className="ngo-disputes-case-title">{dispute.caseTitle}</span>
                          </div>
                        </td>
                        <td>
                          <span className="ngo-disputes-issue-type">{dispute.issueType}</span>
                        </td>
                        <td className="ngo-disputes-date">{dispute.submittedDate}</td>
                        <td>
                          <span className={`ngo-disputes-status-badge ngo-disputes-status-${statusInfo.class}`}>
                            <StatusIcon className="ngo-disputes-status-icon" />
                            {statusInfo.text}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="ngo-disputes-btn-view"
                            onClick={() => handleViewDispute(dispute)}
                          >
                            <Eye className="ngo-disputes-btn-icon" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ngo-disputes-table-footer">
              Showing {filteredDisputes.length} of {disputes.length} disputes
            </div>
          </div>
        </div>
      </div>

      {/* View Dispute Modal */}
      {selectedDispute && !showResolveModal && !showForwardModal && (
        <div className="ngo-disputes-modal-overlay" onClick={() => setSelectedDispute(null)}>
          <div className="ngo-disputes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ngo-disputes-modal-header">
              <h2 className="ngo-disputes-modal-title">Dispute Details</h2>
              <button 
                className="ngo-disputes-modal-close"
                onClick={() => setSelectedDispute(null)}
              >
                <X className="ngo-disputes-icon-close" />
              </button>
            </div>
            
            <div className="ngo-disputes-modal-body">
              {/* Student Info */}
              <div className="ngo-disputes-info-section">
                <h3 className="ngo-disputes-section-title">Student Information</h3>
                <div className="ngo-disputes-info-grid">
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Name:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.studentName}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Email:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.studentEmail}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Phone:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.studentPhone}</span>
                  </div>
                </div>
              </div>

              {/* Case Info */}
              <div className="ngo-disputes-info-section">
                <h3 className="ngo-disputes-section-title">Case Details</h3>
                <div className="ngo-disputes-info-grid">
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Case ID:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.caseId}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Title:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.caseTitle}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Issue Type:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.issueType}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Submitted:</span>
                    <span className="ngo-disputes-info-value">{selectedDispute.submittedDate}</span>
                  </div>
                  <div className="ngo-disputes-info-item">
                    <span className="ngo-disputes-info-label">Status:</span>
                    <span className="ngo-disputes-info-value">
                      {getStatusInfo(selectedDispute.status).text}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="ngo-disputes-info-section">
                <h3 className="ngo-disputes-section-title">Issue Description</h3>
                <p className="ngo-disputes-description">{selectedDispute.description}</p>
              </div>

              {/* Attachment */}
              {selectedDispute.attachment && (
                <div className="ngo-disputes-info-section">
                  <h3 className="ngo-disputes-section-title">Attachment</h3>
                  <div className="ngo-disputes-attachment">
                    <FileText className="ngo-disputes-attachment-icon" />
                    <span className="ngo-disputes-attachment-name">{selectedDispute.attachment}</span>
                    <button className="ngo-disputes-attachment-btn">Download</button>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="ngo-disputes-modal-footer">
              {selectedDispute.status === 'new' && (
                <button 
                  className="ngo-disputes-action-btn ngo-disputes-btn-in-review"
                  onClick={handleMarkInReview}
                >
                  <Clock className="ngo-disputes-action-icon" />
                  Mark as In Review
                </button>
              )}
              
              {(selectedDispute.status === 'new' || selectedDispute.status === 'in-review') && (
                <>
                  <button 
                    className="ngo-disputes-action-btn ngo-disputes-btn-resolve"
                    onClick={() => setShowResolveModal(true)}
                  >
                    <CheckCircle className="ngo-disputes-action-icon" />
                    Resolve
                  </button>
                  
                  <button 
                    className="ngo-disputes-action-btn ngo-disputes-btn-forward"
                    onClick={() => setShowForwardModal(true)}
                  >
                    <Send className="ngo-disputes-action-icon" />
                    Forward to Admin
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="ngo-disputes-modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="ngo-disputes-modal ngo-disputes-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="ngo-disputes-modal-header">
              <h2 className="ngo-disputes-modal-title">Resolve Dispute</h2>
              <button 
                className="ngo-disputes-modal-close"
                onClick={() => setShowResolveModal(false)}
              >
                <X className="ngo-disputes-icon-close" />
              </button>
            </div>
            
            <div className="ngo-disputes-modal-body">
              <label className="ngo-disputes-label">Resolution Comments *</label>
              <textarea
                className="ngo-disputes-textarea"
                rows="5"
                placeholder="Enter resolution details and actions taken..."
                value={resolveComment}
                onChange={(e) => setResolveComment(e.target.value)}
              />
            </div>

            <div className="ngo-disputes-modal-footer">
              <button 
                className="ngo-disputes-action-btn ngo-disputes-btn-cancel"
                onClick={() => setShowResolveModal(false)}
              >
                Cancel
              </button>
              <button 
                className="ngo-disputes-action-btn ngo-disputes-btn-resolve"
                onClick={handleResolve}
              >
                <CheckCircle className="ngo-disputes-action-icon" />
                Mark as Resolved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forward to Admin Modal */}
      {showForwardModal && (
        <div className="ngo-disputes-modal-overlay" onClick={() => setShowForwardModal(false)}>
          <div className="ngo-disputes-modal ngo-disputes-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="ngo-disputes-modal-header">
              <h2 className="ngo-disputes-modal-title">Forward to Admin</h2>
              <button 
                className="ngo-disputes-modal-close"
                onClick={() => setShowForwardModal(false)}
              >
                <X className="ngo-disputes-icon-close" />
              </button>
            </div>
            
            <div className="ngo-disputes-modal-body">
              <label className="ngo-disputes-label">Reason for Forwarding *</label>
              <textarea
                className="ngo-disputes-textarea"
                rows="5"
                placeholder="Example: Payment verification issue – admin approval required"
                value={forwardReason}
                onChange={(e) => setForwardReason(e.target.value)}
              />
              <p className="ngo-disputes-help-text">
                Please provide a clear reason why this dispute needs admin attention.
              </p>
            </div>

            <div className="ngo-disputes-modal-footer">
              <button 
                className="ngo-disputes-action-btn ngo-disputes-btn-cancel"
                onClick={() => setShowForwardModal(false)}
              >
                Cancel
              </button>
              <button 
                className="ngo-disputes-action-btn ngo-disputes-btn-forward"
                onClick={handleForwardToAdmin}
              >
                <Send className="ngo-disputes-action-icon" />
                Forward to Admin
              </button>
            </div>
          </div>
        </div>
      )}
    </NgoLayout>
  );
};

export default NgoDisputes;